import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, TrendingUp, Users, Calendar, BarChart3, Banknote, ArrowDownToLine, Loader2 } from 'lucide-react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  CartesianGrid,
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface FeeEvent {
  date: string;
  amount: number;
  partnerName: string;
  accountValueBefore: number;
  accountValueAfter: number;
  hwmBefore: number;
  hwmAfter: number;
}

interface FeeWithdrawal {
  id: string;
  withdrawal_date: string;
  amount: number;
  notes: string | null;
  created_at: string;
}

interface PartnerAccount {
  id: string;
  user_id: string;
  current_value: number;
  total_invested: number;
  performance_fee_pct: number;
  high_water_mark: number;
  total_fees_paid: number;
  fee_history: unknown;
  profiles: { full_name: string; email: string } | null;
}

interface FeeTarget {
  id: string;
  account_value: number;
  high_water_mark: number;
}

interface PerformanceFeesTabProps {
  partnerAccounts: PartnerAccount[];
  targets?: FeeTarget[];
  formatCurrency: (value: number) => string;
  onWithdrawalRecorded?: () => void | Promise<void>;
}

// Helper to parse fee history
const parseFeeHistory = (history: unknown): FeeEvent[] => {
  if (!history || !Array.isArray(history)) return [];
  return history.filter((event): event is FeeEvent =>
    typeof event === 'object' && event !== null && 'date' in event && 'amount' in event
  );
};

const PerformanceFeesTab: React.FC<PerformanceFeesTabProps> = ({ partnerAccounts, targets = [], formatCurrency, onWithdrawalRecorded }) => {
  const { toast } = useToast();
  
  // Withdrawal state
  const [withdrawals, setWithdrawals] = useState<FeeWithdrawal[]>([]);
  const [isLoadingWithdrawals, setIsLoadingWithdrawals] = useState(true);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalNotes, setWithdrawalNotes] = useState('');
  const [isSubmittingWithdrawal, setIsSubmittingWithdrawal] = useState(false);

  // Fetch withdrawals on mount
  useEffect(() => {
    const fetchWithdrawals = async () => {
      try {
        const { data, error } = await supabase
          .from('fee_withdrawals')
          .select('*')
          .order('withdrawal_date', { ascending: false });
        
        if (error) throw error;
        setWithdrawals(data || []);
      } catch (error) {
        console.error('Error fetching withdrawals:', error);
      } finally {
        setIsLoadingWithdrawals(false);
      }
    };
    fetchWithdrawals();
  }, []);

  // Handle withdrawal submission
  const handleWithdrawal = async () => {
    if (!withdrawalAmount || isNaN(parseFloat(withdrawalAmount)) || parseFloat(withdrawalAmount) <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid withdrawal amount.",
        variant: "destructive",
      });
      return;
    }

    const amount = parseFloat(withdrawalAmount);
    
    if (amount > availableToWithdraw) {
      toast({
        title: "Insufficient Funds",
        description: `You can only withdraw up to ${formatCurrency(availableToWithdraw)}.`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingWithdrawal(true);

    try {
      const { data, error } = await supabase
        .from('fee_withdrawals')
        .insert({
          amount: amount,
          notes: withdrawalNotes || null,
          withdrawal_date: new Date().toISOString().split('T')[0],
        })
        .select()
        .single();

      if (error) throw error;

      // Deduct the withdrawn fees from fund value and HWM, proportionally by value
      const fundPartners = partnerAccounts.filter(a => a.current_value > 0);
      const partnerTotal = fundPartners.reduce((s, a) => s + a.current_value, 0);
      const today = new Date().toISOString().split('T')[0];
      for (const acc of fundPartners) {
        const share = partnerTotal > 0 ? acc.current_value / partnerTotal : 0;
        const cut = amount * share;
        const newValue = acc.current_value - cut;
        const history = Array.isArray((acc as any).performance_history) ? (acc as any).performance_history : [];
        await supabase.from('partner_accounts').update({
          current_value: newValue,
          high_water_mark: Math.max(0, (acc.high_water_mark || 0) - cut),
          performance_history: [...history, { date: today, value: newValue, event: 'withdrawal', event_amount: cut, note: 'fee withdrawal' }],
          last_updated: new Date().toISOString(),
        }).eq('id', acc.id);
      }
      const targetTotal = targets.reduce((s, t) => s + (t.account_value || 0), 0);
      for (const t of targets) {
        const share = targetTotal > 0 ? (t.account_value || 0) / targetTotal : 0;
        const cut = amount * share;
        await supabase.from('trade_targets').update({
          account_value: (t.account_value || 0) - cut,
          high_water_mark: Math.max(0, (t.high_water_mark || 0) - cut),
        }).eq('id', t.id);
      }
      await onWithdrawalRecorded?.();

      setWithdrawals(prev => [data, ...prev]);
      setWithdrawalAmount('');
      setWithdrawalNotes('');

      toast({
        title: "Withdrawal Recorded",
        description: `Successfully recorded ${formatCurrency(amount)} withdrawal.`,
      });
    } catch (error: any) {
      console.error('Error recording withdrawal:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to record withdrawal.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingWithdrawal(false);
    }
  };

  // Filter to active partners only
  const activePartners = partnerAccounts.filter(a => a.current_value > 0 || a.total_fees_paid > 0);

  // Calculate total fees collected across all partners
  const totalFeesCollected = useMemo(() => {
    return activePartners.reduce((sum, acc) => sum + (acc.total_fees_paid || 0), 0);
  }, [activePartners]);

  // Calculate total withdrawn
  const totalWithdrawn = useMemo(() => {
    return withdrawals.reduce((sum, w) => sum + w.amount, 0);
  }, [withdrawals]);

  // Available to withdraw
  const availableToWithdraw = totalFeesCollected - totalWithdrawn;

  // Aggregate all fee events across partners for time-series analysis
  const allFeeEvents = useMemo(() => {
    const events: (FeeEvent & { partnerId: string })[] = [];
    activePartners.forEach(account => {
      const history = parseFeeHistory(account.fee_history);
      history.forEach(event => {
        events.push({
          ...event,
          partnerId: account.id,
        });
      });
    });
    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [activePartners]);

  // Group fees by month for chart
  const monthlyFees = useMemo(() => {
    const byMonth: Record<string, number> = {};
    allFeeEvents.forEach(event => {
      const month = event.date.substring(0, 7); // YYYY-MM
      byMonth[month] = (byMonth[month] || 0) + event.amount;
    });
    return Object.entries(byMonth)
      .map(([month, amount]) => ({
        month,
        label: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        amount,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [allFeeEvents]);

  // Group withdrawals by month
  const monthlyWithdrawals = useMemo(() => {
    const byMonth: Record<string, number> = {};
    withdrawals.forEach(w => {
      const month = w.withdrawal_date.substring(0, 7);
      byMonth[month] = (byMonth[month] || 0) + w.amount;
    });
    return byMonth;
  }, [withdrawals]);

  // Combined monthly data for chart
  const monthlyData = useMemo(() => {
    const allMonths = new Set([
      ...monthlyFees.map(f => f.month),
      ...Object.keys(monthlyWithdrawals),
    ]);
    return Array.from(allMonths)
      .sort()
      .map(month => ({
        month,
        label: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        collected: monthlyFees.find(f => f.month === month)?.amount || 0,
        withdrawn: monthlyWithdrawals[month] || 0,
      }));
  }, [monthlyFees, monthlyWithdrawals]);

  // Calculate cumulative fees over time for area chart
  const cumulativeFees = useMemo(() => {
    let cumulative = 0;
    let cumulativeWithdrawn = 0;
    return monthlyData.map(item => {
      cumulative += item.collected;
      cumulativeWithdrawn += item.withdrawn;
      return {
        ...item,
        cumulative,
        cumulativeWithdrawn,
        available: cumulative - cumulativeWithdrawn,
      };
    });
  }, [monthlyData]);

  // Calculate quarterly totals
  const quarterlyFees = useMemo(() => {
    const byQuarter: Record<string, { collected: number; withdrawn: number }> = {};
    
    allFeeEvents.forEach(event => {
      const date = new Date(event.date);
      const year = date.getFullYear();
      const quarter = Math.ceil((date.getMonth() + 1) / 3);
      const key = `${year}-Q${quarter}`;
      if (!byQuarter[key]) byQuarter[key] = { collected: 0, withdrawn: 0 };
      byQuarter[key].collected += event.amount;
    });
    
    withdrawals.forEach(w => {
      const date = new Date(w.withdrawal_date);
      const year = date.getFullYear();
      const quarter = Math.ceil((date.getMonth() + 1) / 3);
      const key = `${year}-Q${quarter}`;
      if (!byQuarter[key]) byQuarter[key] = { collected: 0, withdrawn: 0 };
      byQuarter[key].withdrawn += w.amount;
    });
    
    return Object.entries(byQuarter)
      .map(([quarter, data]) => ({ quarter, ...data }))
      .sort((a, b) => a.quarter.localeCompare(b.quarter));
  }, [allFeeEvents, withdrawals]);

  // Calculate YTD fees and withdrawals
  const currentYear = new Date().getFullYear().toString();
  
  const ytdFees = useMemo(() => {
    return allFeeEvents
      .filter(event => event.date.startsWith(currentYear))
      .reduce((sum, event) => sum + event.amount, 0);
  }, [allFeeEvents, currentYear]);

  const ytdWithdrawals = useMemo(() => {
    return withdrawals
      .filter(w => w.withdrawal_date.startsWith(currentYear))
      .reduce((sum, w) => sum + w.amount, 0);
  }, [withdrawals, currentYear]);

  // Partners with potential fees (above HWM)
  const partnersAboveHWM = activePartners.filter(
    acc => acc.current_value > acc.high_water_mark
  );

  // Potential unrealized fees
  const potentialFees = useMemo(() => {
    return partnersAboveHWM.reduce((sum, acc) => {
      const profit = acc.current_value - acc.high_water_mark;
      const fee = profit * (acc.performance_fee_pct / 100);
      return sum + fee;
    }, 0);
  }, [partnersAboveHWM]);

  const chartConfig = {
    collected: { label: 'Collected', color: 'hsl(var(--delphi-red))' },
    withdrawn: { label: 'Withdrawn', color: 'hsl(142 76% 36%)' },
    available: { label: 'Available', color: 'hsl(48 96% 53%)' },
    cumulative: { label: 'Cumulative', color: 'hsl(var(--delphi-red))' },
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30">
          <CardHeader className="pb-2 px-3 md:px-6">
            <CardTitle className="text-xs md:text-sm text-delphi-light/70 flex items-center">
              <DollarSign size={14} className="text-delphi-red mr-1 md:mr-2 flex-shrink-0" />
              <span className="truncate">Total Collected</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 md:px-6 pt-0">
            <p className="text-lg md:text-2xl font-bold text-delphi-light">{formatCurrency(totalFeesCollected)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-green-500/30">
          <CardHeader className="pb-2 px-3 md:px-6">
            <CardTitle className="text-xs md:text-sm text-delphi-light/70 flex items-center">
              <Banknote size={14} className="text-green-500 mr-1 md:mr-2 flex-shrink-0" />
              <span className="truncate">Withdrawn</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 md:px-6 pt-0">
            <p className="text-lg md:text-2xl font-bold text-green-500">{formatCurrency(totalWithdrawn)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-yellow-500/30">
          <CardHeader className="pb-2 px-3 md:px-6">
            <CardTitle className="text-xs md:text-sm text-delphi-light/70 flex items-center">
              <ArrowDownToLine size={14} className="text-yellow-500 mr-1 md:mr-2 flex-shrink-0" />
              <span className="truncate">Available</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 md:px-6 pt-0">
            <p className="text-lg md:text-2xl font-bold text-yellow-500">{formatCurrency(availableToWithdraw)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30">
          <CardHeader className="pb-2 px-3 md:px-6">
            <CardTitle className="text-xs md:text-sm text-delphi-light/70 flex items-center">
              <Calendar size={14} className="text-delphi-red mr-1 md:mr-2 flex-shrink-0" />
              <span className="truncate">YTD</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 md:px-6 pt-0">
            <p className="text-sm md:text-lg font-bold text-delphi-light">
              {formatCurrency(ytdFees)} <span className="text-delphi-light/50 hidden md:inline">/</span> <span className="text-green-500 hidden md:inline">{formatCurrency(ytdWithdrawals)}</span>
            </p>
            <p className="text-green-500 text-xs md:hidden mt-1">-{formatCurrency(ytdWithdrawals)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/30">
          <CardHeader className="pb-2 px-3 md:px-6">
            <CardTitle className="text-xs md:text-sm text-delphi-light/70 flex items-center">
              <TrendingUp size={14} className="text-green-500 mr-1 md:mr-2 flex-shrink-0" />
              <span className="truncate">Potential Fees</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 md:px-6 pt-0">
            <p className="text-lg md:text-2xl font-bold text-green-500">{formatCurrency(potentialFees)}</p>
            <p className="text-xs text-delphi-light/50 mt-1">
              {partnersAboveHWM.length} above HWM
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Withdrawal Form */}
      <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-green-500/30">
        <CardHeader>
          <CardTitle className="text-lg text-delphi-light flex items-center">
            <Banknote size={18} className="mr-2 text-green-500" />
            Record Fee Withdrawal
          </CardTitle>
          <CardDescription>Record when you withdraw earned fees to your bank account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-delphi-light/70 text-sm mb-2 block">Withdrawal Amount</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-delphi-light/70 text-sm mb-2 block">Notes (optional)</Label>
              <Input
                placeholder="e.g., Q4 2025 withdrawal"
                value={withdrawalNotes}
                onChange={(e) => setWithdrawalNotes(e.target.value)}
                className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
              />
            </div>
            <Button
              onClick={handleWithdrawal}
              disabled={isSubmittingWithdrawal || availableToWithdraw <= 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isSubmittingWithdrawal ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowDownToLine size={16} className="mr-2" />
              )}
              Record Withdrawal
            </Button>
          </div>
          {availableToWithdraw <= 0 && (
            <p className="text-yellow-500 text-sm mt-3">No fees available to withdraw at this time.</p>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cumulative Fees Chart */}
        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20">
          <CardHeader>
            <CardTitle className="text-lg text-delphi-light flex items-center">
              <BarChart3 size={18} className="mr-2 text-delphi-red" />
              Cumulative Fees vs Withdrawals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cumulativeFees.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-[250px] w-full">
                <AreaChart data={cumulativeFees} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="label" 
                    stroke="rgba(255,255,255,0.5)" 
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.5)" 
                    fontSize={12}
                    tickLine={false}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent />}
                    formatter={(value, name) => {
                      const labels: Record<string, string> = {
                        cumulative: 'Total Collected',
                        cumulativeWithdrawn: 'Total Withdrawn',
                        available: 'Available',
                      };
                      return [`$${Number(value).toLocaleString()}`, labels[name as string] || name];
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulative"
                    stroke="#e53935"
                    fill="rgba(229, 57, 53, 0.2)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulativeWithdrawn"
                    stroke="#22c55e"
                    fill="rgba(34, 197, 94, 0.2)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-delphi-light/50">
                No fee data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Fees Bar Chart */}
        <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20">
          <CardHeader>
            <CardTitle className="text-lg text-delphi-light flex items-center">
              <BarChart3 size={18} className="mr-2 text-delphi-red" />
              Monthly Collection & Withdrawals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-[250px] w-full">
                <BarChart data={monthlyData} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="label" 
                    stroke="rgba(255,255,255,0.5)" 
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.5)" 
                    fontSize={12}
                    tickLine={false}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent />}
                    formatter={(value, name) => {
                      const labels: Record<string, string> = {
                        collected: 'Collected',
                        withdrawn: 'Withdrawn',
                      };
                      return [`$${Number(value).toLocaleString()}`, labels[name as string] || name];
                    }}
                  />
                  <Bar dataKey="collected" fill="#e53935" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="withdrawn" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-delphi-light/50">
                No fee data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Partner Fee Details Table */}
      <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20">
        <CardHeader>
          <CardTitle className="text-lg text-delphi-light">Partner Fee Details</CardTitle>
          <CardDescription>Individual partner performance fee tracking with high water marks</CardDescription>
        </CardHeader>
        <CardContent>
          {activePartners.length === 0 ? (
            <p className="text-delphi-light/50 text-center py-8">No partner data available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-delphi-gray/30">
                    <th className="text-left py-3 px-3 text-delphi-light/70 font-medium">Partner</th>
                    <th className="text-right py-3 px-3 text-delphi-light/70 font-medium">Current Value</th>
                    <th className="text-right py-3 px-3 text-delphi-light/70 font-medium">High Water Mark</th>
                    <th className="text-right py-3 px-3 text-delphi-light/70 font-medium">Above/Below HWM</th>
                    <th className="text-right py-3 px-3 text-delphi-light/70 font-medium">Fee %</th>
                    <th className="text-right py-3 px-3 text-delphi-light/70 font-medium">Total Fees Paid</th>
                    <th className="text-right py-3 px-3 text-delphi-light/70 font-medium">Potential Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {activePartners.map((account) => {
                    const aboveHWM = account.current_value - account.high_water_mark;
                    const potentialFee = aboveHWM > 0 ? aboveHWM * (account.performance_fee_pct / 100) : 0;
                    
                    return (
                      <tr key={account.id} className="border-b border-delphi-gray/10 hover:bg-delphi-red/5">
                        <td className="py-3 px-3">
                          <p className="text-delphi-light font-medium">{account.profiles?.full_name || 'Unknown'}</p>
                          <p className="text-delphi-light/50 text-sm">{account.profiles?.email}</p>
                        </td>
                        <td className="text-right py-3 px-3 text-delphi-light font-medium">
                          {formatCurrency(account.current_value)}
                        </td>
                        <td className="text-right py-3 px-3 text-delphi-light/70">
                          {formatCurrency(account.high_water_mark)}
                        </td>
                        <td className={`text-right py-3 px-3 font-medium ${
                          aboveHWM >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {aboveHWM >= 0 ? '+' : ''}{formatCurrency(aboveHWM)}
                        </td>
                        <td className="text-right py-3 px-3 text-delphi-red font-medium">
                          {account.performance_fee_pct}%
                        </td>
                        <td className="text-right py-3 px-3 text-delphi-light font-medium">
                          {formatCurrency(account.total_fees_paid || 0)}
                        </td>
                        <td className="text-right py-3 px-3 text-green-500/80">
                          {potentialFee > 0 ? formatCurrency(potentialFee) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-delphi-red/30 bg-delphi-red/5">
                    <td className="py-3 px-3 font-bold text-delphi-light">TOTALS</td>
                    <td className="text-right py-3 px-3 font-bold text-delphi-light">
                      {formatCurrency(activePartners.reduce((sum, a) => sum + a.current_value, 0))}
                    </td>
                    <td className="text-right py-3 px-3 font-bold text-delphi-light">
                      {formatCurrency(activePartners.reduce((sum, a) => sum + a.high_water_mark, 0))}
                    </td>
                    <td className="py-3 px-3"></td>
                    <td className="py-3 px-3"></td>
                    <td className="text-right py-3 px-3 font-bold text-delphi-light">
                      {formatCurrency(totalFeesCollected)}
                    </td>
                    <td className="text-right py-3 px-3 font-bold text-green-500">
                      {formatCurrency(potentialFees)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quarterly Summary Table */}
      <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-delphi-red/20">
        <CardHeader>
          <CardTitle className="text-lg text-delphi-light">Quarterly & YTD Summary</CardTitle>
          <CardDescription>Fee collection and withdrawals by quarter</CardDescription>
        </CardHeader>
        <CardContent>
          {quarterlyFees.length === 0 ? (
            <p className="text-delphi-light/50 text-center py-8">No quarterly data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-delphi-gray/30">
                    <th className="text-left py-3 px-4 text-delphi-light/70 font-medium">Period</th>
                    <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Fees Collected</th>
                    <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Withdrawn</th>
                    <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {quarterlyFees.map(({ quarter, collected, withdrawn }) => (
                    <tr key={quarter} className="border-b border-delphi-gray/10 hover:bg-delphi-red/5">
                      <td className="py-3 px-4 text-delphi-light font-medium">{quarter}</td>
                      <td className="text-right py-3 px-4 text-delphi-light font-medium">
                        {formatCurrency(collected)}
                      </td>
                      <td className="text-right py-3 px-4 text-green-500 font-medium">
                        {withdrawn > 0 ? formatCurrency(withdrawn) : '-'}
                      </td>
                      <td className={`text-right py-3 px-4 font-medium ${
                        collected - withdrawn >= 0 ? 'text-delphi-light' : 'text-red-500'
                      }`}>
                        {formatCurrency(collected - withdrawn)}
                      </td>
                    </tr>
                  ))}
                  {/* YTD Row */}
                  <tr className="border-t-2 border-delphi-red/30 bg-delphi-red/5">
                    <td className="py-3 px-4 font-bold text-delphi-light">
                      YTD ({new Date().getFullYear()})
                    </td>
                    <td className="text-right py-3 px-4 font-bold text-delphi-light">
                      {formatCurrency(ytdFees)}
                    </td>
                    <td className="text-right py-3 px-4 font-bold text-green-500">
                      {formatCurrency(ytdWithdrawals)}
                    </td>
                    <td className="text-right py-3 px-4 font-bold text-delphi-light">
                      {formatCurrency(ytdFees - ytdWithdrawals)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Withdrawal History */}
      <Card className="bg-gradient-to-br from-delphi-dark to-delphi-dark/80 border border-green-500/20">
        <CardHeader>
          <CardTitle className="text-lg text-delphi-light flex items-center">
            <Banknote size={18} className="mr-2 text-green-500" />
            Withdrawal History
          </CardTitle>
          <CardDescription>Record of all fee withdrawals to bank</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingWithdrawals ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-green-500" />
            </div>
          ) : withdrawals.length === 0 ? (
            <p className="text-delphi-light/50 text-center py-8">No withdrawals recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-delphi-gray/30">
                    <th className="text-left py-3 px-4 text-delphi-light/70 font-medium">Date</th>
                    <th className="text-right py-3 px-4 text-delphi-light/70 font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-delphi-light/70 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((w) => (
                    <tr key={w.id} className="border-b border-delphi-gray/10 hover:bg-green-500/5">
                      <td className="py-3 px-4 text-delphi-light">
                        {new Date(w.withdrawal_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="text-right py-3 px-4 text-green-500 font-medium">
                        {formatCurrency(w.amount)}
                      </td>
                      <td className="py-3 px-4 text-delphi-light/70">
                        {w.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-green-500/30 bg-green-500/5">
                    <td className="py-3 px-4 font-bold text-delphi-light">TOTAL WITHDRAWN</td>
                    <td className="text-right py-3 px-4 font-bold text-green-500">
                      {formatCurrency(totalWithdrawn)}
                    </td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceFeesTab;